import {SaveAltOutlined} from "@mui/icons-material";
import {Alert, Box, Button, Grid, IconButton, Snackbar, Typography} from "@mui/material";
import {
    DataGrid,
    GridDeleteIcon,
    GridToolbarColumnsButton,
    GridToolbarContainer,
} from "@mui/x-data-grid";
import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import MyBooleanMark from "../../components/MyBooleanMark";
import PageHeader from "../../components/PageHeader";
import {REP_TYPES} from "../../constants";
import {useEvents} from "../../hooks/useEvents";
import {useEventsDownloadCsv} from "../../hooks/useEventsDownloadCsv";
import {useLocalStorage} from "../../hooks/useLocalStorage";
import routes from "../../routes";
import {getCurrentUser} from "../../util/AuthUtil";
import {getInactiveRowClassName, inactiveRowStyles} from "../../util/DataGridUtil";
import {disableSortingAndMenu} from "../../util/FilterOperatorsUtil";
import {toPercentage} from "../../util/NumberUtil";
import EventDeleteModal from "./EventDeleteModal";
import EventModal from "./EventModal";
import EventsFilter from "./EventsFilter";

const defaultColumnWidths = {
    address: 357,
    eventType: 147,
    productType: 164,
    indoorOutdoor: 245,
    shiptoName: 234,
    shipto: 146,
    currencyCode: 198,
    languageCode: 179,
    golfPro: 248,
    onsiteClubBuilding: 220,
    shareLink: 567,
    fitterCount: 168,
    businessUnitCode: 193,
    bookabilityLevel: 228,
    bookedPercentage: 195,
    eventTypeName: 209,
    onsiteBuildStation: 224,
    fittingsCount: 177,
    fittingDurationOpt: 232,
    specsVisibility: 198,
    id: 73,
    scheduledStart: 201,
    scheduledEnd: 216,
    locationName: 272,
    active: 120,
    completedPercentage: 188,
    action: 100,
    salesTerritory: 180,
    region: 202,
    salesRep: 185,
    createdUser: 280,
};

const EventsTable = () => {
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 20,
    });

    const [filter, setFilter] = useState({
        fromDate: "",
        toDate: "",
        businessUnitCode: "",
        salesRegion: "",
        salesTerritory: "",
    });
    const filterChangeFunctions = useMemo(() => ({
        searchString: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                searchString: value,
            }));
        },
        fromDate: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                fromDate: value,
            }));
        },
        toDate: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                toDate: value,
            }));
        },
        businessUnitCode: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                businessUnitCode: value,
            }));
        },
        salesRegion: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                salesRegion: value,
                salesTerritory: "",
            }));
        },
        salesTerritory: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                salesTerritory: value,
            }));
        },
    }), []);
    const navigate = useNavigate();
    const currentUser = getCurrentUser();

    useEffect(() => {
        if (!routes.find((route) => route.key === "events").visibleRepTypes.includes(currentUser.repType.id)) {
            navigate(REP_TYPES[currentUser.repType.id].defaultRoute);
        }
    }, [currentUser, navigate]);

    const [eventColumnWidths, setEventColumnWidths] = useLocalStorage("eventColumnWidths", {});
    const [eventDetail, setEventDetail] = useState(null);
    const repModelRef = useRef();
    const repDeleteModelRef = useRef();
    const onShowEventDetail = (event) => {
        setEventDetail(event);
        repModelRef.current.handleOpen();
    };
    const onShowModalDeleteEvent = (event) => {
        setEventDetail(event);
        repDeleteModelRef.current.handleOpen();
    };
    const handleClickDeleteEvent = (event) => {
        onShowModalDeleteEvent(event);
    };

    const columns = useMemo(() => {
        let header = [
            {
                field: "id",
                headerName: "ID",
                width: eventColumnWidths.id ?? defaultColumnWidths.id,
            },
            {
                field: "scheduledStart",
                headerName: "START TIME",
                width: eventColumnWidths.scheduledStart ?? defaultColumnWidths.scheduledStart,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleString();
                    } else {
                        return "Unknown";
                    }
                },
            },
            {
                field: "scheduledEnd",
                headerName: "END TIME",
                width: eventColumnWidths.scheduledEnd ?? defaultColumnWidths.scheduledEnd,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleString();
                    } else {
                        return "Unknown";
                    }
                },
            },
            {
                field: "locationName",
                headerName: "LOCATION",
                width: eventColumnWidths.locationName ?? defaultColumnWidths.locationName,
                renderCell: (params) => {
                    if (params.row.facility) {
                        return params.row.facility.name;
                    } else if (params.row.location) {
                        return params.row.location.name;
                    } else {
                        return "Unknown";
                    }
                },
            },
            {
                field: "address",
                headerName: "ADDRESS",
                width: eventColumnWidths.address ?? defaultColumnWidths.address,
                renderCell: (params) => {
                    if (params.row.facility) {
                        let facility = params.row.facility;
                        return [
                            facility.address1,
                            facility.address2,
                            facility.city,
                            facility.state,
                            facility.postalCode,
                        ]
                            .filter(Boolean)
                            .join(", ");
                    } else if (params.row.location) {
                        let location = params.row.location;
                        return [
                            location.address1,
                            location.address2,
                            location.city,
                            location.state,
                            location.postalCode,
                        ]
                            .filter(Boolean)
                            .join(", ");
                    } else {
                        return "Unknown";
                    }
                },
            },
            {
                field: "eventType",
                headerName: "EVENT TYPE",
                width: eventColumnWidths.eventType ?? defaultColumnWidths.eventType,
                renderCell: (params) => {
                    const {eventType} = params.row;
                    let eventTypeName = eventType?.name;
                    return eventTypeName ?? "";
                },
            },
            {
                field: "productType",
                headerName: "PRODUCT TYPE",
                width: eventColumnWidths.productType ?? defaultColumnWidths.productType,
                renderCell: (params) => {
                    const {eventProductType} = params.row;
                    let eventProductTypeName = eventProductType?.name;
                    return eventProductTypeName ?? "";
                },
            },
            {
                field: "indoorOutdoor",
                headerName: "INDOOR / OUTDOOR",
                width: eventColumnWidths.indoorOutdoor ?? defaultColumnWidths.indoorOutdoor,
                renderCell: (params) => {
                    const {indoor} = params.row;
                    let indoorOutdoor = indoor === null ? null : indoor ? "Indoor" : "Outdoor";
                    return indoorOutdoor ?? "";
                },
            },
            {
                field: "shipto",
                headerName: "SHIP TO",
                width: eventColumnWidths.shipto ?? defaultColumnWidths.shipto,
                renderCell: (params) => {
                    let shipto = params.row.shipto;
                    return shipto ? shipto.shiptoId : "Unknown";
                },
            },
            {
                field: "shiptoName",
                headerName: "SHIP TO NAME",
                width: eventColumnWidths.shiptoName ?? defaultColumnWidths.shiptoName,
                renderCell: (params) => {
                    let shipto = params.row.shipto;
                    return shipto ? shipto.name : "Unknown";
                },
            },
            {
                field: "golfPro",
                headerName: "GOLF PRO",
                width: eventColumnWidths.golfPro ?? defaultColumnWidths.golfPro,
                renderCell: (params) => {
                    // const {eventAdmins} = params.row;
                    // let golfPro;
                    // if (eventAdmins) {
                    //     golfPro = eventAdmins.find(admin => admin.isPro);
                    // }
                    // return golfPro ? [golfPro.firstName, golfPro.lastName].filter(Boolean).join(' ') + ' (' + golfPro.email + ')' : 'None';
                    const {golfProFirstName, golfProLastName, golfProEmail} = params.row;
                    let email = golfProEmail ? ` (${golfProEmail})` : "";
                    return [golfProFirstName, golfProLastName].filter(Boolean).join(" ") + email;
                },
            },
            {
                field: "onsiteBuildStation",
                width: eventColumnWidths.onsiteBuildStation ?? defaultColumnWidths.onsiteBuildStation,
                headerName: "BUILD STATION",
                renderCell: (params) => {
                    const {onsiteBuildStation} = params.row;
                    if (!onsiteBuildStation) {
                        return "";
                    }
                    return onsiteBuildStation?.name || "";
                },
            },
            {
                field: "shareLink",
                headerName: "SHARE LINK",
                width: eventColumnWidths.shareLink ?? defaultColumnWidths.shareLink,
            },
            {
                field: "fitterCount",
                headerName: "FITTER COUNT",
                width: eventColumnWidths.fitterCount ?? defaultColumnWidths.fitterCount,
            },
            {
                field: "businessUnitCode",
                headerName: "BUSINESS UNIT",
                width: eventColumnWidths.businessUnitCode ?? defaultColumnWidths.businessUnitCode,
            },
            {
                field: "active",
                headerName: "ACTIVE",
                width: eventColumnWidths.active ?? defaultColumnWidths.active,
                renderCell: (params) => <MyBooleanMark value={params.value} />,
            },
            {
                field: "languageCode",
                headerName: "LANGUAGE",
                width: eventColumnWidths.languageCode ?? defaultColumnWidths.languageCode,
            },
            {
                field: "currencyCode",
                headerName: "CURRENCY CODE",
                width: eventColumnWidths.currencyCode ?? defaultColumnWidths.currencyCode,
            },
            {
                field: "bookabilityLevel",
                headerName: "BOOKABILITY LEVEL",
                width: eventColumnWidths.bookabilityLevel ?? defaultColumnWidths.bookabilityLevel,
                renderCell: (params) => {
                    return params.value ? params.value.bookabilityLevelName : "Unknown";
                },
            },
            {
                field: "specsVisibility",
                headerName: "SPECS VISIBILITY",
                width: eventColumnWidths.specsVisibility ?? defaultColumnWidths.specsVisibility,
                renderCell: (params) => {
                    return params.value ? params.value.name : "Unknown";
                },
            },
            {
                field: "fittingDurationOpt",
                headerName: "FITTING DURATION",
                width: eventColumnWidths.fittingDurationOpt ?? defaultColumnWidths.fittingDurationOpt,
                renderCell: (params) => {
                    return params.value ? params.value.name : "Unknown";
                },
            },
            {
                field: "fittingsCount",
                headerName: "FITTINGS COUNT",
                width: eventColumnWidths.fittingsCount ?? defaultColumnWidths.fittingsCount,
            },
            {
                field: "bookedPercentage",
                headerName: "BOOKED PERCENTAGE",
                width: eventColumnWidths.bookedPercentage ?? defaultColumnWidths.bookedPercentage,
                renderCell: (params) => {
                    return toPercentage(params.value);
                },
            },
            {
                field: "completedPercentage",
                headerName: "COMPLETED PERCENTAGE",
                width: eventColumnWidths.completedPercentage ?? defaultColumnWidths.completedPercentage,
                renderCell: (params) => {
                    return toPercentage(params.value);
                },
            },
            {
                field: "region",
                headerName: "REGION",
                width: eventColumnWidths.region ?? defaultColumnWidths.region,
                renderCell: (params) => {
                    const salesRegion = params?.row?.shipto?.salesRegion;

                    if (salesRegion) {
                        const color = salesRegion?.active == true ? "black" : "grey";
                        return <Box color={color}>{salesRegion?.name}</Box>;
                    } else {
                        return "Unknown";
                    }
                },
            },
            {
                field: "salesTerritory",
                headerName: "SALES TERRITORY",
                width: eventColumnWidths.salesTerritory ?? defaultColumnWidths.salesTerritory,
                renderCell: (params) => {
                    const salesTerritory = params?.row?.shipto?.salesTerritory;
                    if (salesTerritory) {
                        return salesTerritory?.friendlyName;
                    } else {
                        return "Unknown";
                    }
                },
            },
            {
                field: "salesRep",
                headerName: "SALES REP",
                width: eventColumnWidths.salesRep ?? defaultColumnWidths.salesRep,
                renderCell: (params) => {
                    const salesRep = params?.row?.shipto?.salesRep;
                    if (salesRep) {
                        const color = salesRep?.activated == true ? "black" : "grey";
                        return (
                            <Box color={color}>
                                {`${salesRep?.firstName} ${salesRep.lastName} ${salesRep?.email ? `(${salesRep?.email})` : ""}`}
                            </Box>
                        );
                    } else {
                        return "Unknown";
                    }
                },
            },
            {
                field: "experientialRep",
                headerName: "EXPERIENTIAL REP",
                width: eventColumnWidths.supportRep ?? defaultColumnWidths.supportRep,
                renderCell: (params) => {
                    const supportRep = params?.row?.shipto?.supportRep;
                    if (supportRep) {
                        const color = supportRep?.activated == true ? "black" : "grey";
                        return (
                            <Box color={color}>
                                {`${supportRep?.firstName} ${supportRep.lastName} ${supportRep?.email ? `(${supportRep?.email})` : ""}`}
                            </Box>
                        );
                    } else {
                        return "Unknown";
                    }
                },
            },
            {
                field: "createdUser",
                headerName: "CREATE USER",
                width: eventColumnWidths.createdUser ?? defaultColumnWidths.createdUser,
                renderCell: (params) => {
                    const createdUser = params?.row?.createdUser;
                    if (createdUser) {
                        const color = createdUser?.activated == true ? "black" : "grey";
                        return (
                            <Box color={color}>
                                {`${createdUser?.firstName} ${createdUser.lastName} ${createdUser?.email ? `(${createdUser?.email})` : ""}`}
                            </Box>
                        );
                    } else {
                        return "Unknown";
                    }
                },
            },
            {
                field: "actions",
                headerName: "ACTIONS",
                width: eventColumnWidths.action ?? defaultColumnWidths.action,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                disableReorder: true,
                disableExport: true,
                headerAlign: "center",
                align: "center",
                renderCell: (params) => {
                    if (!params.row.active) {
                        return "";
                    }
                    return (
                        <Box>
                            <IconButton
                                color="error"
                                size="small"
                                title="Delete event"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleClickDeleteEvent(params.row);
                                }}
                                aria-label="delete"
                            >
                                <GridDeleteIcon />
                            </IconButton>
                        </Box>
                    );
                },
            },
        ];
        disableSortingAndMenu(header);
        return header;
    }, []);

    const [events, totalRowCount, refetchEvents] = useEvents({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        fromDate: filter.fromDate,
        toDate: filter.toDate,
        salesRegionId: filter.salesRegion,
        salesTerritoryId: filter.salesTerritory,
        searchString: filter.searchString,
    });

    const handleColumnResize = (params) => {
        setEventColumnWidths((prevWidths) => ({
            ...prevWidths,
            [params.colDef.field]: params.width,
        }));
    };

    function renderControlPanel() {
        return (
            <Box pt={1}>
                <Grid
                    container
                    spacing={1}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Grid item xs={12}>
                        <EventsFilter filter={filter} filterChangeFunctions={filterChangeFunctions} />
                    </Grid>
                </Grid>
            </Box>
        );
    }
    const [exportEventToCSV, isExportEventToCSV, isErrorExport] = useEventsDownloadCsv(
        {fromDate: filter.fromDate, toDate: filter.toDate},
        false
    );
    const [showErrorExportEvents, setShowErrorExportEvents] = useState(false);

    useEffect(() => {
        setShowErrorExportEvents(isErrorExport);
    }, [isErrorExport]);

    const handleExportAllData = async () => {
        setShowErrorExportEvents(false);
        exportEventToCSV();
    };
    function CustomToolbar() {
        const isFilterApplied = filter.fromDate || filter.toDate;
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                {isFilterApplied && (
                    <Button
                        size="small"
                        disabled={isExportEventToCSV}
                        startIcon={<SaveAltOutlined />}
                        onClick={handleExportAllData}
                    >
                        {isExportEventToCSV ? "Exporting..." : "Export to CSV"}
                    </Button>
                )}
            </GridToolbarContainer>
        );
    }
    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <PageHeader title="Manage Events" children={renderControlPanel()} />

                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                    }}
                >
                    <DataGrid
                        columns={columns}
                        rows={events}
                        onRowClick={({row}) => onShowEventDetail(row)}
                        paginationMode="server"
                        rowCount={totalRowCount}
                        pageSizeOptions={[10, 20, 50]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        density="compact"
                        slots={{toolbar: CustomToolbar}}
                        disableRowSelectionOnClick={true}
                        onColumnWidthChange={handleColumnResize}
                        getRowClassName={getInactiveRowClassName("active")}
                        sx={{
                            height: "100%",
                            position: "absolute",
                            width: "100%",
                            "& .MuiDataGrid-cell:focus": {
                                outline: "none",
                            },
                            "& .MuiDataGrid-cell:focus-within": {
                                outline: "none",
                            },
                            "& .MuiDataGrid-row": {
                                cursor: "pointer",
                            },
                            ...inactiveRowStyles,
                            // fontSize: '1rem',
                        }}
                    />
                </Box>
            </Box>
            <EventModal ref={repModelRef} eventDetail={eventDetail} />
            <EventDeleteModal ref={repDeleteModelRef} refetchEvents={refetchEvents} eventDetail={eventDetail} />
            <Snackbar
                open={showErrorExportEvents}
                autoHideDuration={6000}
                onClose={() => setShowErrorExportEvents(false)}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert onClose={() => setShowErrorExportEvents(false)} severity="error" sx={{width: "100%"}}>
                    Error export to csv
                </Alert>
            </Snackbar>
        </>
    );
};

export default EventsTable;
